import { useEffect, useState } from 'react'
import { usePostHog } from '@posthog/react'

import { FEATURE_FLAGS, resolveFlagName } from '@/common/utils/FeatureFlags/FeatureFlags'

const readFlags = (posthog) =>
    Object.entries(FEATURE_FLAGS).reduce((acc, [key, baseName]) => {
        const value = posthog.getFeatureFlag(resolveFlagName(baseName))
        // Boolean flags return true/false, multivariate flags return their variant string, and
        // undefined means "not loaded yet" or "PostHog unreachable". Collapsing undefined to
        // false makes hidden the safe default, so a blocked request (PostHog is a common
        // ad-blocker target) never leaks an unreleased feature.
        acc[key] = value === undefined ? false : value
        return acc
    }, {})

/**
 * useFeatureFlags - Resolves the PostHog feature-flag registry (FEATURE_FLAGS).
 *
 * @returns {Object} Every key in FEATURE_FLAGS (boolean, or the variant string for
 *   multivariate flags), plus `isReady` — false until PostHog has loaded flags at least once.
 */
export function useFeatureFlags() {
    const posthog = usePostHog()
    const [flags, setFlags] = useState(() => ({ isReady: false, ...readFlags(posthog) }))

    useEffect(() => {
        // Fires immediately if flags are already cached from a previous session, then again on
        // every reload (identify/group both trigger one). Returns its own unsubscribe.
        return posthog.onFeatureFlags(() => setFlags({ isReady: true, ...readFlags(posthog) }))
    }, [posthog])

    return flags
}
